import React, { useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { string, bool, arrayOf, oneOf, shape, func } from 'prop-types';
import classnames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { useTrackChange } from './hooks/trackChange';
import { useMixpanelEvents } from './hooks/mixpanel';
import { useBigQueryEvents } from './hooks/bigQuery';
import { PRELOADS } from './constants';
import Sources from './components/Sources';
import Subtitles from './components/Subtitles';

import './index.scss';

/**
 * @deprecated Please use Video from '@fiverr-private/media' instead.
 */
const Video = forwardRef(({
    className,
    controls,
    source,
    poster,
    preload,
    autoPlay,
    loop,
    muted,
    playsInline,
    subtitles,
    sourceInfo,
    onPlay,
    onPause,
}, ref) => {
    const { locale } = getContext();
    const [subtitleLocale, setSubtitleLocale] = useState(locale);
    const videoRef = useRef(null);
    const baseMeta = {
        autoPlay,
        subtitleLocale: subtitles.length > 0 ? subtitleLocale : null,
        subtitleExist: subtitles.length > 0,
    };
    const mixpanelMeta = useRef({});
    const bigQueryMeta = useRef({});

    mixpanelMeta.current = {
        ...baseMeta,
        pageName: sourceInfo.mixpanel.pageName,
        videoName: sourceInfo.videoName,
    };
    bigQueryMeta.current = {
        ...baseMeta,
        pageName: sourceInfo.bigQuery.pageName,
        videoName: sourceInfo.videoName,
    };

    useImperativeHandle(ref, () => videoRef.current);

    useMixpanelEvents(videoRef, mixpanelMeta);
    useBigQueryEvents(videoRef, bigQueryMeta);

    useTrackChange(
        videoRef,
        useCallback((locale) => {
            setSubtitleLocale(locale);
        }, [])
    );

    return (
        <video className={classnames('orca-video', className)}
            ref={videoRef}
            autoPlay={autoPlay}
            loop={loop}
            muted={muted}
            playsInline={playsInline}
            controls={controls}
            poster={poster}
            preload={preload}
            crossOrigin="anonymous"
            onPlay={onPlay}
            onPause={onPause}>
            <Sources sources={source}/>
            <Subtitles subtitles={subtitles}/>
        </video>
    );
});

Video.PRELOADS = PRELOADS;

Video.displayName = 'Video';

Video.propTypes = {
    /**
     * Whether or not the Video has controls.
     */
    controls: bool,

    /**
     * Video sources. Shape: {
     * src: video link |
     * type: video type
     * }
     */
    source: arrayOf(
        shape({
            src: string.isRequired,
            type: string,
        })
    ).isRequired,

    /**
     * Top element class
     */
    className: string,

    /**
     * A URL for an image to be shown while the video is downloading
     */
    poster: string,

    /**
     * Video preload phase, available as `Video.PRELOADS`
     */
    preload: oneOf(['none', 'metadata', 'auto']),

    /**
     * Video auto play on render
     */
    autoPlay: bool,

    /**
     * Auto replay
     */
    loop: bool,

    /**
     * Whether or not the Video audio is muted
     */
    muted: bool,

    /**
     * Whether or not play the video within the element's playback area
     */
    playsInline: bool,

    /**
     * Video subtitles - user locale active by default.<br/>
     * Shape: {<br/>
     * - label: string - Subtitle selection name |<br/>
     * - src: string - Subtitle src |<br/>
     * - srcLang: string - Subtitle locale (BCP 47 format)<br/>
     * }<br/>
     * { @link https://en.wikipedia.org/wiki/IETF_language_tag }
     */
    subtitles: arrayOf(
        shape({
            src: string,
            srcLang: string,
            label: string,
        })
    ),

    /**
     * BI params
     */
    sourceInfo: shape({
        videoName: string,
        mixpanel: shape({
            pageName: string,
        }),
        bigQuery: shape({
            pageName: string,
        }),
    }),

    /**
     * onPlay https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play_event
     */
    onPlay: func,

    /**
     * onPause https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/pause_event
     */
    onPause: func,
};

Video.defaultProps = {
    className: '',
    controls: true,
    poster: '',
    preload: 'metadata',
    autoPlay: false,
    subtitles: [],
    sourceInfo: {
        mixpanel: {},
        bigQuery: {},
    },
    source: [],
    onPlay: Function.prototype,
    onPause: Function.prototype,
};

export default Video;
